import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { axiosInstance } from "../App";
import { AlertTriangle, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";

const AnomalyWidget = ({ onViewAll }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axiosInstance.get("/anomalies/summary");
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch anomaly summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    if (type?.includes("drop")) return <TrendingDown className="w-4 h-4" />;
    return <TrendingUp className="w-4 h-4" />;
  };

  const getTypeColor = (type) => {
    if (type === "revenue_drop" || type === "expense_spike") return "text-red-600";
    return "text-green-600";
  };

  const getTypeLabel = (type) => {
    const labels = {
      revenue_drop: "Rev ↓",
      revenue_spike: "Rev ↑",
      expense_drop: "Exp ↓",
      expense_spike: "Exp ↑"
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="py-6 text-center text-gray-400">
          Loading anomalies...
        </CardContent>
      </Card>
    );
  }

  if (!summary || summary.total_active === 0) {
    return null; // Don't show widget if no active anomalies
  }

  return (
    <Card className="shadow-lg border-l-4 border-l-orange-500" data-testid="anomaly-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Anomalies
            <Badge variant="destructive" className="ml-1">
              {summary.total_active} Open
            </Badge>
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onViewAll}
            className="text-xs text-orange-600 hover:text-orange-700 gap-1"
            data-testid="view-all-anomalies"
          >
            View All <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {summary.recent_anomalies?.slice(0, 3).map((anomaly, idx) => (
            <div 
              key={anomaly.id || idx}
              className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm hover:bg-gray-100 cursor-pointer"
              onClick={onViewAll}
            >
              <div className="flex items-center gap-2">
                <span className={getTypeColor(anomaly.type)}>
                  {getTypeIcon(anomaly.type)}
                </span>
                <span className="font-medium truncate max-w-[120px]">
                  {anomaly.restaurant_name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {getTypeLabel(anomaly.type)}
                </span>
                <span className={`font-semibold ${anomaly.deviation_percentage < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {anomaly.deviation_percentage > 0 ? '+' : ''}{anomaly.deviation_percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default AnomalyWidget;
